import { useEffect, useMemo, useRef, useState } from 'react'
import { useGridFilter, type CustomFilterProps } from 'ag-grid-react'
import type { IRowNode } from 'ag-grid-community'

type FilterModel = { values: string[] }

interface CheckboxSetFilterParams {
  // Группировка значений по году/месяцу (как в Excel). Ожидает строки вида
  // dd.mm.yyyy или dd.mm.yy. Прочие значения уходят в секцию «Без даты».
  dateMode?: boolean
}

const MONTH_LABELS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
]

const EMPTY_KEY = '(пусто)'

type DateParts = { year: number; month: number; raw: string }
type YearGroup = { year: number; months: Map<number, string[]> }

function parseDate(raw: string): DateParts | null {
  const trimmed = raw.trim()
  // dd.mm.yy | dd.mm.yyyy — основной формат в UI
  const ru = /^(\d{2})\.(\d{2})\.(\d{2,4})$/.exec(trimmed)
  if (ru) {
    const month = Number(ru[2]) - 1
    const yearRaw = Number(ru[3])
    const year = yearRaw < 100 ? 2000 + yearRaw : yearRaw
    if (month < 0 || month > 11) return null
    return { year, month, raw }
  }
  // yyyy-mm-dd или ISO с временем (в TableRow поле date хранится ISO-строкой)
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed)
  if (iso) {
    const month = Number(iso[2]) - 1
    const year = Number(iso[1])
    if (month < 0 || month > 11) return null
    return { year, month, raw }
  }
  return null
}

function buildDateTree(values: string[]): { years: YearGroup[]; ungrouped: string[] } {
  const tree = new Map<number, Map<number, string[]>>()
  const ungrouped: string[] = []
  for (const v of values) {
    if (v === EMPTY_KEY) {
      ungrouped.push(v)
      continue
    }
    const parts = parseDate(v)
    if (!parts) {
      ungrouped.push(v)
      continue
    }
    let months = tree.get(parts.year)
    if (!months) {
      months = new Map()
      tree.set(parts.year, months)
    }
    const days = months.get(parts.month) ?? []
    days.push(v)
    months.set(parts.month, days)
  }
  const years: YearGroup[] = Array.from(tree.entries())
    .map(([year, months]) => {
      for (const [m, days] of months) {
        days.sort((a, b) => a.localeCompare(b))
        months.set(m, days)
      }
      return { year, months }
    })
    .sort((a, b) => b.year - a.year)
  return { years, ungrouped }
}

export function CheckboxSetFilter({
  api,
  colDef,
  model,
  onModelChange,
}: CustomFilterProps<unknown, unknown, FilterModel>) {
  const params = (colDef.filterParams ?? {}) as CheckboxSetFilterParams
  const dateMode = params.dateMode ?? false

  const allValuesRef = useRef<string[]>([])
  const selectedRef = useRef<Set<string>>(new Set())
  const savedSelectionRef = useRef<Set<string> | null>(null)
  const [, forceRender] = useState(0)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  useEffect(() => {
    const vals = new Set<string>()
    api.forEachNode((node: IRowNode) => {
      const v = (node.data as Record<string, unknown> | undefined)?.[
        colDef.field as unknown as string
      ]
      vals.add(v == null || v === '' ? EMPTY_KEY : String(v))
    })
    const sorted = Array.from(vals).sort()
    allValuesRef.current = sorted
    selectedRef.current = new Set(sorted)
    forceRender((n) => n + 1)
  }, [api, colDef.field])

  useEffect(() => {
    if (model === null) {
      selectedRef.current = new Set(allValuesRef.current)
    } else {
      selectedRef.current = new Set(model.values)
    }
    forceRender((n) => n + 1)
  }, [model])

  useGridFilter({
    doesFilterPass({ node }: { node: IRowNode }) {
      const v = (node.data as Record<string, unknown> | undefined)?.[
        colDef.field as unknown as string
      ]
      const key = v == null || v === '' ? EMPTY_KEY : String(v)
      return selectedRef.current.has(key)
    },
  })

  const applySelection = (next: Set<string>) => {
    selectedRef.current = next
    forceRender((n) => n + 1)
    const active = next.size < allValuesRef.current.length
    onModelChange(active ? { values: Array.from(next) } : null)
  }

  const toggleValues = (values: string[]) => {
    const next = new Set(selectedRef.current)
    const allSelected = values.every((v) => next.has(v))
    if (allSelected) {
      for (const v of values) next.delete(v)
    } else {
      for (const v of values) next.add(v)
    }
    applySelection(next)
  }

  const selectAll = () => {
    selectedRef.current = new Set(allValuesRef.current)
    forceRender((n) => n + 1)
    onModelChange(null)
  }

  const clearAll = () => {
    selectedRef.current = new Set<string>()
    forceRender((n) => n + 1)
    onModelChange({ values: [] })
  }

  const [search, setSearch] = useState('')
  const allValues = allValuesRef.current
  const selected = selectedRef.current
  const filtered = search
    ? allValues.filter((v) => v.toLowerCase().trim().includes(search.toLowerCase().trim()))
    : allValues

  const handleSearch = (q: string) => {
    setSearch(q)
    if (q) {
      if (savedSelectionRef.current === null) {
        savedSelectionRef.current = new Set(selectedRef.current)
      }
      const matching = new Set(
        allValues.filter((v) => v.toLowerCase().trim().includes(q.toLowerCase().trim())),
      )
      selectedRef.current = matching
      forceRender((n) => n + 1)
      onModelChange(matching.size < allValues.length ? { values: Array.from(matching) } : null)
    } else {
      const restored = savedSelectionRef.current ?? new Set(allValues)
      savedSelectionRef.current = null
      selectedRef.current = restored
      forceRender((n) => n + 1)
      const active = restored.size < allValues.length
      onModelChange(active ? { values: Array.from(restored) } : null)
    }
  }

  const dateTree = useMemo(() => (dateMode ? buildDateTree(filtered) : null), [dateMode, filtered])

  const toggleExpand = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <div style={containerStyle}>
      <input
        type="text"
        value={search}
        onChange={(e) => handleSearch(e.target.value)}
        placeholder="Поиск..."
        autoFocus
        style={inputStyle}
      />
      <div style={actionsRowStyle}>
        <button onClick={selectAll} style={linkButtonStyle}>
          Выбрать все
        </button>
        <button onClick={clearAll} style={linkButtonStyle}>
          Сбросить
        </button>
      </div>
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {filtered.length === 0 && <div style={emptyStyle}>Нет данных</div>}
        {dateTree ? (
          <DateTreeView
            tree={dateTree}
            expanded={expanded}
            selected={selected}
            onToggleExpand={toggleExpand}
            onToggleValues={toggleValues}
          />
        ) : (
          filtered.map((val) => (
            <label key={val} style={rowStyle}>
              <input
                type="checkbox"
                checked={selected.has(val)}
                onChange={() => toggleValues([val])}
                style={{ cursor: 'pointer' }}
              />
              {val}
            </label>
          ))
        )}
      </div>
    </div>
  )
}
CheckboxSetFilter.displayName = 'CheckboxSetFilter'

interface DateTreeViewProps {
  tree: { years: YearGroup[]; ungrouped: string[] }
  expanded: Set<string>
  selected: Set<string>
  onToggleExpand: (key: string) => void
  onToggleValues: (values: string[]) => void
}

function DateTreeView({
  tree,
  expanded,
  selected,
  onToggleExpand,
  onToggleValues,
}: DateTreeViewProps) {
  return (
    <>
      {tree.years.map((yearGroup) => {
        const yearValues: string[] = []
        for (const days of yearGroup.months.values()) yearValues.push(...days)
        const yearKey = `y-${yearGroup.year}`
        const isYearOpen = expanded.has(yearKey)
        return (
          <div key={yearKey}>
            <TreeRow
              depth={0}
              open={isYearOpen}
              label={String(yearGroup.year)}
              values={yearValues}
              selected={selected}
              onToggleExpand={() => onToggleExpand(yearKey)}
              onToggleValues={onToggleValues}
            />
            {isYearOpen &&
              Array.from(yearGroup.months.entries())
                .sort((a, b) => a[0] - b[0])
                .map(([monthIdx, days]) => {
                  const monthKey = `m-${yearGroup.year}-${monthIdx}`
                  const isMonthOpen = expanded.has(monthKey)
                  return (
                    <div key={monthKey}>
                      <TreeRow
                        depth={1}
                        open={isMonthOpen}
                        label={MONTH_LABELS[monthIdx]!}
                        values={days}
                        selected={selected}
                        onToggleExpand={() => onToggleExpand(monthKey)}
                        onToggleValues={onToggleValues}
                      />
                      {isMonthOpen &&
                        days.map((d) => (
                          <label key={d} style={{ ...rowStyle, paddingLeft: 36 }}>
                            <input
                              type="checkbox"
                              checked={selected.has(d)}
                              onChange={() => onToggleValues([d])}
                              style={{ cursor: 'pointer' }}
                            />
                            {d}
                          </label>
                        ))}
                    </div>
                  )
                })}
          </div>
        )
      })}
      {tree.ungrouped.length > 0 && (
        <div style={{ marginTop: tree.years.length > 0 ? 6 : 0 }}>
          {tree.ungrouped.map((val) => (
            <label key={val} style={rowStyle}>
              <input
                type="checkbox"
                checked={selected.has(val)}
                onChange={() => onToggleValues([val])}
                style={{ cursor: 'pointer' }}
              />
              {val === EMPTY_KEY ? '(без даты)' : val}
            </label>
          ))}
        </div>
      )}
    </>
  )
}

interface TreeRowProps {
  depth: number
  open: boolean
  label: string
  values: string[]
  selected: Set<string>
  onToggleExpand: () => void
  onToggleValues: (values: string[]) => void
}

function TreeRow({
  depth,
  open,
  label,
  values,
  selected,
  onToggleExpand,
  onToggleValues,
}: TreeRowProps) {
  const selectedCount = values.reduce((acc, v) => (selected.has(v) ? acc + 1 : acc), 0)
  const allChecked = selectedCount === values.length
  const someChecked = selectedCount > 0 && selectedCount < values.length
  // Нативный indeterminate доступен только через ref — применяем через callback.
  const setIndeterminate = (el: HTMLInputElement | null) => {
    if (el) el.indeterminate = someChecked
  }
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 0',
        paddingLeft: depth * 18,
      }}
    >
      <button
        onClick={onToggleExpand}
        style={{
          width: 14,
          height: 14,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          color: 'var(--text-muted)',
          fontSize: 10,
        }}
        aria-label={open ? 'Свернуть' : 'Развернуть'}
      >
        {open ? '▼' : '▶'}
      </button>
      <input
        type="checkbox"
        checked={allChecked}
        ref={setIndeterminate}
        onChange={() => onToggleValues(values)}
        style={{ cursor: 'pointer' }}
      />
      <span
        onClick={onToggleExpand}
        style={{ cursor: 'pointer', userSelect: 'none', fontWeight: depth === 0 ? 600 : 500 }}
      >
        {label}
      </span>
    </div>
  )
}

// Цвета берём из CSS-переменных темы (см. index.css) — иначе попап остаётся
// белым в тёмной теме и текст значений сливается с фоном (был баг: фильтры
// в Клиентах/Заявках нечитаемы ночью).
const containerStyle = {
  padding: 8,
  minWidth: 200,
  fontSize: 13,
  background: 'var(--bg-card)',
  color: 'var(--text)',
  borderRadius: 8,
} as const

const inputStyle = {
  width: '100%',
  marginBottom: 6,
  padding: '3px 6px',
  fontSize: 12,
  border: '1px solid var(--border-strong)',
  borderRadius: 3,
  background: 'var(--bg-app)',
  color: 'var(--text)',
  boxSizing: 'border-box' as const,
  outline: 'none',
}

const actionsRowStyle = {
  display: 'flex',
  gap: 10,
  marginBottom: 6,
  paddingBottom: 6,
  borderBottom: '1px solid var(--border)',
}

const linkButtonStyle = {
  fontSize: 12,
  color: '#3b82f6',
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  padding: 0,
}

const emptyStyle = {
  color: 'var(--text-faint)',
  fontStyle: 'italic' as const,
  fontSize: 12,
}

const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '3px 0',
  cursor: 'pointer',
}
